// SalesForecast — Phase 3A Sales tab (the Sales/forecast spine) + Phase 1 review.
//
// 12-month moving horizon (Australia/Sydney): current-6 .. current+5
// (covers later P6 + F6). Completed months show actual sales; the current
// and future months show forecast = previous-month × 1.03, cascading.
//
// Actuals provenance (closed months):
//   • approved (manual)   — actual_units set by hand            → drives forecast
//   • approved (shopify)  — actual_units accepted from ingest   → drives forecast
//   • ingested / review   — actual_ingested present, not yet accepted (Shopify
//                            auto-pull stages here) → SHOWN but does NOT drive
//                            the forecast until a human Accepts or Overrides
//   • missing             — nothing recorded
// actual_units ALWAYS wins over actual_ingested. A warn/fail badge (checkActual)
// can sit on any cell. The Shopify ingest Lambda (later phase) writes only
// actual_ingested; this UI never depends on it being present (works with zero
// ingested rows today).
//
// The cascade is computed on read (pure fn of stored approved actuals + overrides
// + growth) — no rollover Lambda needed. Manual edits upsert to
// forecaster_sales_monthly; clear a cell (blank + Enter) to revert.
//
// NOTE: this tab never touches SOH. Scope stays narrow (Phase 3A); On-hand
// planning, Arriving, replenishment, transactions and projected DOH are NOT here.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { aws } from '../lib/awsdb';
import { authWrite } from '../lib/authWrite';
import { actualState, classifySku, checkActual } from './salesCheck';
import { fetchLiveSkuOverlay } from '../lib/liveSkuOverlay';
import { downloadXlsx } from '../lib/exportXlsx';
import { logExportActivity } from '../lib/exportActivityLog';
import { Button } from '../ui/button';
import { IconDownload } from '../ui/icons';

const SERIF = '\'Tiempos\', Georgia, \'Times New Roman\', serif';
const MONO  = '\'JetBrains Mono\', ui-monospace, \'SF Mono\', Menlo, monospace';
const GROWTH_DEFAULT = 1.03;

// ── Australia/Sydney calendar-month helpers (timezone-safe) ──
function auYearMonth(d = new Date()) {
  const parts = new Intl.DateTimeFormat('en-AU', { timeZone: 'Australia/Sydney', year: 'numeric', month: '2-digit' }).formatToParts(d);
  return { y: +parts.find(p => p.type === 'year').value, m: +parts.find(p => p.type === 'month').value };
}
function shift(y, m, delta) { const i = y * 12 + (m - 1) + delta; return { y: Math.floor(i / 12), m: (i % 12) + 1 }; }
function monthKey(y, m) { return `${y}-${String(m).padStart(2, '0')}-01`; }
function monthLabel(y, m) { return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-AU', { month: 'short', year: '2-digit' }); }
const round = (n) => (n == null ? null : Math.round(n));

// 12-month moving horizon: current-6 .. current+5.
function buildMonths() {
  const cur = auYearMonth();
  return Array.from({ length: 12 }, (_, i) => {
    const delta = i - 6;
    const { y, m } = shift(cur.y, cur.m, delta);
    return { key: monthKey(y, m), label: monthLabel(y, m), isClosed: delta < 0, isCurrent: delta === 0 };
  });
}

// tag → label + colour. Provenance tags (approved_*/ingested/missing) for closed
// months; computed/override for forecast months.
const LABEL = {
  approved_manual:  { t: 'manual',    c: '#0F8E6F' },
  approved_shopify: { t: 'shopify ✓', c: '#0F8E6F' },
  ingested:         { t: 'review',    c: '#2563EB' },
  computed:         { t: 'computed',  c: '#6E7278' },
  override:         { t: 'override',  c: '#A56B00' },
  missing:          { t: '—',         c: '#C5C3BC' },
};
const thStyle = { padding: '8px 10px', fontFamily: MONO, fontSize: 9.5, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6E7278', borderBottom: '1px solid #E8E6E0', textAlign: 'right', whiteSpace: 'nowrap' };
const tdStyle = { padding: '6px 10px', textAlign: 'right', whiteSpace: 'nowrap' };

export default function SalesForecast({ session, skus = [], catalog = [] }) {
  const canWrite = session?.role === 'admin' || session?.permissions?.stock_write !== false;
  const months = useMemo(buildMonths, []);
  const [rows, setRows] = useState([]);   // forecaster_sales_monthly
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null); // { skuId, key, kind:'actual'|'forecast', value:string }
  const [busy, setBusy] = useState(false);
  const [liveOverlay, setLiveOverlay] = useState({}); // sku_code -> { month: {units} } — display-only, never written

  useEffect(() => {
    let cancelled = false;
    fetchLiveSkuOverlay(months).then(o => { if (!cancelled) setLiveOverlay(o); }).catch(() => {});
    return () => { cancelled = true; };
  }, [months]);

  const reload = useCallback(async () => {
    setLoading(true);
    const { data } = await aws.from('forecaster_sales_monthly').select('*');
    setRows(data || []);
    setLoading(false);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const byKey = useMemo(() => {
    const m = {};
    for (const r of rows) m[`${r.sku_id}|${r.month}`] = r;
    return m;
  }, [rows]);

  // catalog_id → catalog row, for SKU classification (bundle/accessory/sub).
  const catalogById = useMemo(() => {
    const m = {};
    for (const c of catalog) m[c.id] = c;
    return m;
  }, [catalog]);
  const skuKindOf = useCallback((sku) => classifySku(catalogById[sku.catalog_id]), [catalogById]);

  // Cascade + provenance + review verdict across the window for one SKU.
  // `basis` (forecast cascade) uses APPROVED actual_units ONLY — an unreviewed
  // actual_ingested never moves the forecast. `priorEff` (variance context) uses
  // the effective actual (approved ?? ingested) so checks work pre-approval.
  function cells(skuId, skuKind, skuCode) {
    let basis = null;
    const priorEff = [];
    const liveByMonth = skuCode ? liveOverlay[skuCode] : null;
    return months.map(mo => {
      const rec = byKey[`${skuId}|${mo.key}`] || null;
      if (mo.isClosed) {
        const approved = rec?.actual_units != null ? Number(rec.actual_units) : null;
        const ingested = rec?.actual_ingested != null ? Number(rec.actual_ingested) : null;
        let value = approved != null ? approved : ingested;     // units wins (display)
        if (approved != null) basis = approved;                   // basis = approved only
        let tag = actualState(approved, ingested, rec?.actual_source);
        // Display-only fallback: if the forecaster has nothing at all for this
        // cell, but the live Shopify snapshot has a real number for this SKU,
        // show it exactly like an ingested/review cell — never written back,
        // never feeds the forecast basis (basis was already settled above).
        const live = tag === 'missing' ? liveByMonth?.[mo.key.slice(0, 7)] : null;
        if (live) { value = live.units; tag = 'ingested'; }
        const check = checkActual({ value, isClosed: true, priorActuals: priorEff.slice(), skuKind });
        if (value != null) priorEff.push(value);
        return { mo, kind: 'actual', value, approved, ingested, tag, check, rec, liveEstimate: !!live };
      }
      const growth = rec?.growth_factor != null ? Number(rec.growth_factor) : GROWTH_DEFAULT;
      const override = rec?.forecast_override != null ? Number(rec.forecast_override) : null;
      const computed = basis != null ? round(basis * growth) : null;
      let effective = override != null ? override : computed;
      if (effective != null) basis = effective;                  // basis settled BEFORE any live fallback below
      let tag = override != null ? 'override' : (effective != null ? 'computed' : 'missing');
      let kind = 'forecast';
      let liveEstimate = false;
      if (mo.isCurrent && effective == null) {
        const live = liveByMonth?.[mo.key.slice(0, 7)];
        if (live) { effective = live.units; tag = 'ingested'; kind = 'actual'; liveEstimate = true; }
      }
      return { mo, kind, value: effective, computed, override,
        tag, check: { level: 'pass', reasons: [] }, rec, liveEstimate };
    });
  }

  // Batch review summary across all SKUs × closed months (pure derivation).
  const summary = useMemo(() => {
    const closed = months.filter(m => m.isClosed);
    let approved = 0, review = 0, missing = 0, flagged = 0;
    for (const sku of skus) {
      const skuKind = skuKindOf(sku);
      const priorEff = [];
      for (const mo of closed) {
        const rec = byKey[`${sku.id}|${mo.key}`];
        const a = rec?.actual_units != null ? Number(rec.actual_units) : null;
        const ing = rec?.actual_ingested != null ? Number(rec.actual_ingested) : null;
        const value = a != null ? a : ing;
        const tag = actualState(a, ing, rec?.actual_source);
        if (tag === 'ingested') review++;
        else if (tag === 'missing') missing++;
        else approved++;
        if (checkActual({ value, isClosed: true, priorActuals: priorEff.slice(), skuKind }).level !== 'pass') flagged++;
        if (value != null) priorEff.push(value);
      }
    }
    return { approved, review, missing, flagged };
  }, [skus, byKey, months, skuKindOf]);

  // ── writes (existing authWrite pattern; upsert on sku_id,month) ──
  async function save() {
    if (!edit || busy) return;
    const raw = edit.value.trim();
    const v = raw === '' ? null : Number(raw);
    if (raw !== '' && !Number.isFinite(v)) { setEdit(null); return; }
    setBusy(true);
    try {
      const payload = { sku_id: edit.skuId, month: edit.key, updated_by: session?.name };
      if (edit.kind === 'actual') {
        payload.actual_units = v;                       // manual override / clear
        payload.actual_source = v == null ? null : 'manual';
      } else {
        payload.forecast_override = v;                  // null clears -> reverts to computed
        if (v == null) payload.override_reason = null;
      }
      await authWrite(session, 'forecaster_sales_monthly', 'upsert', payload, null, 'sku_id,month');
      setEdit(null);
      await reload();
    } catch (e) {
      console.error('[SalesForecast] save failed', e);
      alert('Save failed: ' + (e?.message || 'unknown error'));
    } finally { setBusy(false); }
  }

  // Accept a staged Shopify value: copy actual_ingested -> actual_units, mark
  // the approved source as shopify_athena. Reuses the same authWrite upsert.
  async function accept(skuId, key, ingestedVal) {
    if (busy || ingestedVal == null) return;
    setBusy(true);
    try {
      await authWrite(session, 'forecaster_sales_monthly', 'upsert',
        { sku_id: skuId, month: key, actual_units: ingestedVal, actual_source: 'shopify_athena', updated_by: session?.name },
        null, 'sku_id,month');
      setEdit(null);
      await reload();
    } catch (e) {
      console.error('[SalesForecast] accept failed', e);
      alert('Accept failed: ' + (e?.message || 'unknown error'));
    } finally { setBusy(false); }
  }

  function handleDownload() {
    const columns = [
      { label: 'SKU', width: 30, value: r => r.sku_name || '' },
      ...months.map((mo, i) => ({
        label: mo.label,
        width: 11,
        numFmt: '#,##0',
        value: r => {
          const c = cells(r.id, skuKindOf(r), catalogById[r.catalog_id]?.sku_code)[i];
          return c.value != null ? c.value : '';
        },
      })),
    ];
    const filename = `FON_Sales_Forecast_${new Date().toISOString().slice(0, 10)}.xlsx`;
    downloadXlsx(filename, 'Sales Forecast', columns, skus);
    logExportActivity(session, 'Sales Forecast', filename);
  }

  return (
    <div style={{ padding: '8px 40px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 10, flexWrap: 'wrap' }}>
        <h2 style={{ fontFamily: SERIF, fontSize: 20, margin: 0, color: '#0F1419', fontWeight: 400 }}>Sales — 12-month forecast</h2>
        <span style={{ fontSize: 11.5, color: '#6E7278' }}>
          Completed = actual · current + future = forecast (prev × 1.03, cascading) · click a cell to edit, override, or review
        </span>
        {canWrite && (
          <div style={{ marginLeft: 'auto' }}>
            <Button kind="ghost" icon={<IconDownload />} onClick={handleDownload}>Download (Excel)</Button>
          </div>
        )}
      </div>

      {/* Review summary — pure derivation; 0 ingested today is fine. */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontSize: 11, fontFamily: MONO, color: '#6E7278', flexWrap: 'wrap' }}>
        <span><strong style={{ color: '#0F8E6F' }}>{summary.approved}</strong> approved</span>
        <span style={{ color: summary.review ? '#2563EB' : '#6E7278' }}><strong>{summary.review}</strong> to review</span>
        <span style={{ color: summary.flagged ? '#A56B00' : '#6E7278' }}><strong>{summary.flagged}</strong> flagged</span>
        <span><strong>{summary.missing}</strong> missing</span>
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', gap: 14, marginBottom: 10, fontSize: 11, fontFamily: MONO, flexWrap: 'wrap' }}>
        {['approved_manual', 'approved_shopify', 'ingested', 'computed', 'override'].map(k => (
          <span key={k} style={{ color: LABEL[k].c }}>● {LABEL[k].t}</span>
        ))}
        <span style={{ color: '#A56B00' }}>▲ check warning</span>
        <span style={{ color: '#DC2626' }}>✕ check fail</span>
      </div>

      <div style={{ overflowX: 'auto', border: '1px solid #E8E6E0', borderRadius: 10 }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12.5 }}>
          <thead>
            <tr>
              <th style={{ ...thStyle, textAlign: 'left', position: 'sticky', left: 0, background: '#FAFAF8', minWidth: 190, zIndex: 1 }}>SKU</th>
              {months.map(mo => (
                <th key={mo.key} style={{ ...thStyle, color: mo.isCurrent ? '#0F8E6F' : '#6E7278' }}>{mo.label}{mo.isCurrent ? ' •' : ''}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={13} style={{ padding: 20, color: '#9A9DA1' }}>Loading…</td></tr>
            ) : skus.length === 0 ? (
              <tr><td colSpan={13} style={{ padding: 20, color: '#9A9DA1' }}>No SKUs.</td></tr>
            ) : skus.map(sku => {
              const skuKind = skuKindOf(sku);
              const skuCode = catalogById[sku.catalog_id]?.sku_code;
              return (
                <tr key={sku.id} style={{ borderTop: '1px solid #F0EEE8' }}>
                  <td style={{ ...tdStyle, textAlign: 'left', position: 'sticky', left: 0, background: '#fff', fontWeight: 500, minWidth: 190 }}>{sku.sku_name}</td>
                  {cells(sku.id, skuKind, skuCode).map(c => {
                    const isEditing = edit && edit.skuId === sku.id && edit.key === c.mo.key;
                    const lab = LABEL[c.tag] || LABEL.missing;
                    const flag = c.check && c.check.level !== 'pass'
                      ? { glyph: c.check.level === 'fail' ? '✕' : '▲', color: c.check.level === 'fail' ? '#DC2626' : '#A56B00' }
                      : null;
                    const title = [
                      c.kind === 'forecast' && c.override != null ? `override (computed ${c.computed ?? '—'})` : lab.t,
                      c.tag === 'ingested' && !c.liveEstimate ? 'Shopify ingested — needs review (does not drive forecast yet)' : null,
                      c.tag === 'ingested' && c.liveEstimate ? 'Live from Shopify snapshot — needs review (does not drive forecast yet)' : null,
                      flag ? c.check.reasons.join(' · ') : null,
                    ].filter(Boolean).join(' — ');
                    return (
                      <td
                        key={c.mo.key}
                        onClick={() => !isEditing && setEdit({ skuId: sku.id, key: c.mo.key, kind: c.kind, value: c.value == null ? '' : String(c.value) })}
                        title={title}
                        style={{ ...tdStyle, cursor: 'pointer', background: c.mo.isCurrent ? 'rgba(15,142,111,0.04)' : (c.tag === 'ingested' ? 'rgba(37,99,235,0.05)' : '#fff') }}
                      >
                        {isEditing ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-end' }}>
                            <input
                              autoFocus type="number" value={edit.value}
                              onChange={e => setEdit({ ...edit, value: e.target.value })}
                              onBlur={() => setEdit(null)}
                              onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEdit(null); }}
                              style={{ width: 64, fontSize: 12, padding: '2px 4px', border: '1px solid #0F8E6F', borderRadius: 4, fontVariantNumeric: 'tabular-nums' }}
                            />
                            {c.tag === 'ingested' && c.ingested != null && (
                              <button
                                type="button"
                                disabled={busy}
                                onMouseDown={e => { e.preventDefault(); accept(sku.id, c.mo.key, c.ingested); }}
                                style={{ fontSize: 9.5, fontFamily: MONO, padding: '2px 6px', borderRadius: 4, border: '1px solid #2563EB', background: '#2563EB', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}
                              >Accept {c.ingested.toLocaleString()}</button>
                            )}
                            <span style={{ fontSize: 8.5, color: '#9A9DA1', fontFamily: MONO }}>↵ save · esc cancel</span>
                          </div>
                        ) : (
                          <>
                            <div style={{ fontVariantNumeric: 'tabular-nums', color: c.value == null ? '#C5C3BC' : '#0F1419', display: 'flex', gap: 3, justifyContent: 'flex-end', alignItems: 'center' }}>
                              {c.value == null ? '—' : c.value.toLocaleString()}
                              {flag && <span style={{ color: flag.color, fontSize: 10 }}>{flag.glyph}</span>}
                            </div>
                            <div style={{ fontFamily: MONO, fontSize: 8.5, color: lab.c, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{lab.t}</div>
                          </>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: '#9A9DA1' }}>
        Shopify-ingested actuals stage as “review” and don’t drive the forecast until Accepted or Overridden — actual_units always wins.
        Forecast accuracy (MAPE) — coming in a later phase. Clear a cell (blank + Enter) to revert an override or a manual actual.
      </div>
    </div>
  );
}
